import { parse as parseQuery } from 'qs';

import {
  buildEphemeralPrUrl,
  DEFAULT_QUERY_PARAM_EXPIRY_DAYS,
  EXPIRY_DATE_MAX_LENGTH,
  isMaybeProd,
  MFE_EXPIRY_PARAM_SUFFIX,
  MFE_PR_PARAM_SUFFIX,
  MFE_QUERY_PARAM_PREFIX,
  NEVER_EXPIRY_VALUE,
  PR_NUMBER_MAX_LENGTH,
} from './ephemeralMfe';
import { getEndOfDay, writeMfeStorageEntry } from './mfeStorage';

/**
 * SECURITY: Why query params only accept PR numbers, not full URLs
 * ────────────────────────────────────────────────────────────────
 * Query parameters appear in shareable links. If we accepted full URLs (e.g.
 * ?mfe-marketing=https://attacker.com/evil.js), two attacks become trivial:
 *
 * 1. Phishing / open redirect — the link's host is still portal.flipdishdev.com,
 *    so it looks trustworthy in emails and Slack. But the MFE served is fully
 *    attacker-controlled JavaScript running in our origin with access to cookies,
 *    localStorage, and everything the signed-in user can see.
 *
 * 2. Content injection — large arbitrary strings can carry XSS payloads, CSS
 *    injection, SQL fragments, etc. if any downstream code interpolates the value.
 *
 * By accepting ONLY PR numbers (digits, max 10 chars) and building the CDN URL
 * ourselves, we guarantee:
 *   • The loaded script always comes from clients-ephemeral.portal-mfs.flipdishdev.com
 *   • The value space is tiny (≤ 10 digits) — no room for any injection payload
 *   • localhost URLs cannot be injected (no valid PR number maps to localhost)
 *
 * DO NOT extend this to accept full URLs, localhost URLs, or free-text values
 * without a dedicated security review. The toolbar UI (non-shareable, requires
 * physical access to DevTools) is the correct place for those modes.
 */

// Returns undefined to signal "no expiry" (persist forever) for the NEVER_EXPIRY_VALUE sentinel.
const parseExpiresAt = (raw: string): string | undefined => {
  if (raw === NEVER_EXPIRY_VALUE) return undefined;
  if (raw.length > EXPIRY_DATE_MAX_LENGTH) return getEndOfDay(DEFAULT_QUERY_PARAM_EXPIRY_DAYS);
  const parsed = new Date(raw);
  if (isNaN(parsed.getTime()) || parsed <= new Date()) return getEndOfDay(DEFAULT_QUERY_PARAM_EXPIRY_DAYS);
  return parsed.toISOString();
};

export const processMfeQueryParams = (): void => {
  if (isMaybeProd()) return;
  const params = parseQuery(window.location.search.substring(1));

  Object.entries(params).forEach(([key, value]) => {
    if (!key.startsWith(MFE_QUERY_PARAM_PREFIX) || typeof value !== 'string') return;

    const withoutPrefix = key.slice(MFE_QUERY_PARAM_PREFIX.length);

    // Only process params with the -pr suffix: ?mfe-{windowName}-pr=<prNumber>
    if (!withoutPrefix.endsWith(MFE_PR_PARAM_SUFFIX)) return;

    const windowName = withoutPrefix.slice(0, -MFE_PR_PARAM_SUFFIX.length);
    const prNumber = value.trim();

    // Only digits, max PR_NUMBER_MAX_LENGTH characters — see security note above
    if (!/^\d+$/.test(prNumber) || prNumber.length > PR_NUMBER_MAX_LENGTH || prNumber === '0') return;

    const mfeUrl = buildEphemeralPrUrl(windowName, prNumber);

    // Read optional per-MFE expiry param: ?mfe-{windowName}-expiry={isoDate}
    const expiryKey = `${MFE_QUERY_PARAM_PREFIX}${windowName}${MFE_EXPIRY_PARAM_SUFFIX}`;
    const rawExpiry = params[expiryKey];
    const expiresAt =
      typeof rawExpiry === 'string' && rawExpiry.trim()
        ? parseExpiresAt(rawExpiry.trim())
        : getEndOfDay(DEFAULT_QUERY_PARAM_EXPIRY_DAYS);

    writeMfeStorageEntry(windowName, mfeUrl, expiresAt);
  });
};
